import React, { useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Grid,
  withStyles,
  Typography,
} from '@material-ui/core'
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons'

import { LIST_TYPE, ROUTES } from '../constant'
import { triggerCancelTransaction, triggerCounter } from '../actions/app'
import { getTransactions } from '../actions/transaction'
import Inline from '../components/Inline'
import Widget from '../components/Widget'
import Button from '../components/Button'
import Loading from '../components/Loading'
import Section from '../components/Section'
import { ErrorText } from '../components/Text'
import RangeDate from '../components/RangeDate'
import Breadcrumb from '../components/Breadcrumb'
import ModalConfirm from '../components/ModalConfirm'
import TableAutomation from '../components/TableAutomation'


const styles = theme => ({
  widgets: {
    marginTop: theme.spacing(1),
  },
  widget: {
    marginRight: theme.spacing(1),

    '&:last-child': {
      marginRight: 0,
    },
  },
})


const Dashboard = ({
  count,
  items,
  hasMore,
  classes,
  isLoading,
  endBefore,
  startAfter,
  hasPrevious,
  refreshingCollection,
  triggerCounter: triggerCounterAction,
  getTransactions: getTransactionsAction,
  triggerCancelTransaction: triggerCancelTransactionAction,
}) => {
  const [hasChanged, setHasChanged] = useState(false)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [activeTransaction, setActiveTransaction] = useState(null)

  const handleResetActiveTransaction = () => setActiveTransaction(null)

  const handleSetActiveTransaction = (nextTransaction) => () => setActiveTransaction(nextTransaction)

  const handleCancelTransaction = () => {
    triggerCancelTransactionAction(activeTransaction.id)
    handleResetActiveTransaction()
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target

    if (!hasChanged) {
      setHasChanged(true)
    }

    if (name === 'startDate') {
      return setStartDate(value)
    }

    return setEndDate(value)
  }

  const handleGetTransactions = (options = {}) => {
    if (hasChanged) {
      options.endAt = new Date(endDate.toISOString())
      options.startAt = new Date(startDate.toISOString())
    }

    getTransactionsAction(options)
  }

  const createTriggerCounterHandler = collection => () => triggerCounterAction(collection)

  const getStatusByTransaction = (transaction) => {
    if (transaction.canceled) {
      return { data: 'annulé', display: <ErrorText>Annulé</ErrorText> }
    }

    if (transaction.refund) {
      return { data: 'remboursé', display: <Typography color="secondary" variant="caption">Remboursé</Typography> }
    }

    if (transaction.success) {
      return { data: 'réussi', display: <Typography color="secondary" variant="caption">Réussi</Typography> }
    }

    if (!!transaction.handled_at) {
      return { data: 'erreur', display: <ErrorText>Erreur</ErrorText> }
    }

    return { data: 'en attente', display: <Typography variant="caption">En attente</Typography> }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        data={[
          'Accueil',
        ]}
      />

      {isLoading && <Loading />}

      <Grid
        container
        justify="center"
        alignItems="center"
        className={classes.widgets}
      >
        <Widget
          secondary="Commerçants"
          className={classes.widget}
          primary={count.shops || 0}
          isRefreshing={refreshingCollection === 'shops'}
          onRefresh={createTriggerCounterHandler('shops')}
        />
        <Widget
          secondary="Utilisateurs"
          className={classes.widget}
          primary={count.users || 0}
          isRefreshing={refreshingCollection === 'users'}
          onRefresh={createTriggerCounterHandler('users')}
        />
        <Widget
          secondary="de commandes"
          className={classes.widget}
          primary={`${count.orders || 0} €`}
          isRefreshing={refreshingCollection === 'orders'}
          onRefresh={createTriggerCounterHandler('orders')}
        />
        <Widget
          secondary="dépensés"
          className={classes.widget}
          primary={`${count.transactions || 0} €`}
          isRefreshing={refreshingCollection === 'transactions'}
          onRefresh={createTriggerCounterHandler('transactions')}
        />
      </Grid>

      <Section
        disableArrow
        title="Historique des transactions"
      >
        <Inline>
          <RangeDate
            endDate={endDate}
            startDate={startDate}
            onChange={handleDateChange}
            onQuickViewChange={() => handleGetTransactions()}
          />

          <Button
            isLoading={isLoading}
            className={classes.spacing}
            onClick={() => handleGetTransactions()}
          >
            <RefreshIcon />
          </Button>
        </Inline>

        <TableAutomation
          items={items}
          hasMore={hasMore}
          endBefore={endBefore}
          startAfter={startAfter}
          hasPrevious={hasPrevious}
          request={handleGetTransactions}
          csvTitle="Récapitulatif des transactions"
          head={['ID', 'Utilisateur', 'Date', 'Commerce', 'Montant', 'status', 'Actions']}
          row={transaction => {
            const isCancelable = transaction.success && !transaction.refund && !transaction.canceled

            return [
              transaction.id,
              { data: (transaction.user && transaction.user.phone) || transaction.user_id, display: <Link to={`${ROUTES.USER}/${transaction.user_id}`}>{(transaction.user && transaction.user.phone) || transaction.user_id}</Link> },
              transaction.created_at ? moment(transaction.created_at).format('DD/MM/YYYY - HH:mm') : 'N/A',
              { data: (transaction.shop && transaction.shop.name) || transaction.shop_id, display: <Link to={`${ROUTES.MARKET}/${transaction.shop_id}`}>{(transaction.shop && transaction.shop.name) || transaction.shop_id}</Link> },
              `${transaction.amount || 0} €`,
              getStatusByTransaction(transaction),
              {
                data: '',
                display: isCancelable ? (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSetActiveTransaction(transaction)}
                  >
                    <DeleteIcon />
                  </Button>
                ) : <span />,
              },
            ]
          }}
        />
      </Section>

      <ModalConfirm
        isLoading={isLoading}
        open={!!activeTransaction}
        onSubmit={handleCancelTransaction}
        onClose={handleResetActiveTransaction}
        submitLabel="Annuler la transaction"
        title="Annuler une transaction"
      >
        <Typography>Voulez-vous annuler la transaction et rembourser l'utilisateur ?</Typography>
      </ModalConfirm>
    </div>
  )
}


Dashboard.propTypes = {
  count: PropTypes.object,
  hasMore: PropTypes.bool,
  endBefore: PropTypes.any,
  isLoading: PropTypes.bool,
  startAfter: PropTypes.any,
  hasPrevious: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  refreshingCollection: PropTypes.string,
  triggerCounter: PropTypes.func.isRequired,
  getTransactions: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.object),
  triggerCancelTransaction: PropTypes.func.isRequired,
}


const mapStateToProps = state => ({
  count: state.app.count,
  isLoading: state.app.isLoading,
  endBefore: state.list.endBefore,
  startAfter: state.list.startAfter,
  refreshingCollection: state.app.refreshingCollection,
  items: state.list.type === LIST_TYPE.TRANSACTIONS ? state.list.items : [],
  hasMore: state.list.type === LIST_TYPE.TRANSACTIONS ? state.list.hasMore : false,
  hasPrevious: state.list.type === LIST_TYPE.TRANSACTIONS ? state.list.hasPrevious : false,
})


export default connect(mapStateToProps, {
  triggerCounter,
  getTransactions,
  triggerCancelTransaction,
})(withStyles(styles)(Dashboard))
